import React from 'react';
import { useState, useEffect } from 'react';
import { Button } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import * as authActions from 'app/auth/store/actions';
import { API_BASE_URL } from '@config';
import LocalLoginForm from './LocalLoginForm';
import LdapLoginForm from './LdapLoginForm';
import DividerWithText from '../util/DividerWithText';
import OidcLoginForm from './OidcLoginForm';
import { OPENID_PROVIDERS } from '../util/enums';
import { startOidcLogin } from '../util/openIdConnect';

const CustomButton = ({ text, onClick, style }) => (
    <Button
        variant="outlined"
        style={{
            borderColor: 'grey',
            borderWidth: '1px',
            color: 'black',
            margin: '10px 0',
            textTransform: 'none',
            boxShadow: 'none',
            transition: 'background-color 0.3s',
            ...style,
        }}
        fullWidth
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#eaeaea')}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        onClick={onClick}
    >
        {text}
    </Button>
);

const LoginTab = ({ props, setRecoverPassword, recoverPassword, reg }) => {
    const [selectedLoginForm, setSelectedLoginForm] = useState('local');
    const dispatch = useDispatch();
    const activeOidcProviders = useSelector(({ auth }) => auth.auth.activeOidcProviders);

    // Enabled providers drive which shortcut buttons appear. With nothing enabled -
    // the default on every install - this resolves to an empty list and the page looks
    // exactly as it always has.
    const enabledConfigs = (activeOidcProviders || []).filter(config => config.enabled);
    const oktaConfigs = enabledConfigs.filter(config => config.provider === OPENID_PROVIDERS.OKTA);
    // Replace the generic route only when a single Okta provider is the whole of the
    // OpenID configuration. Any other combination keeps the dropdown, so no provider
    // that works today becomes unreachable.
    const oktaIsOnlyProvider = oktaConfigs.length === 1 && enabledConfigs.length === 1;

    useEffect(() => {
        dispatch(authActions.getActiveOidcProviders());
    }, [dispatch]);

    // A plain render helper, deliberately NOT a component declared inside this
    // one: an inline component is a new type on every render, so React would
    // remount the whole form (and wipe what was typed) each time the provider
    // list in the store changed - which happens on every failed sign-in.
    const renderLoginForm = () => {
        switch (selectedLoginForm) {
            case 'local':
                return (
                    <>
                        <LocalLoginForm {...props} setRecoverPassword={setRecoverPassword} recoverPassword={recoverPassword} />
                        {!recoverPassword && (
                            <>
                                <DividerWithText text="Or" style={{ margin: '30px 0' }} />
                                {/* One Okta provider: send the user straight to it rather than
                                    making them pick from a list of one. More than one, or a
                                    different provider, keeps the generic route below. The local
                                    form above stays reachable either way, so a misconfigured
                                    identity provider cannot lock an administrator out. */}
                                {oktaIsOnlyProvider && (
                                    <CustomButton
                                        text={`Sign in with ${oktaConfigs[0].label || 'Okta'}`}
                                        onClick={() => startOidcLogin(oktaConfigs[0], API_BASE_URL)}
                                    />
                                )}
                                <CustomButton text="Sign in with LDAP" onClick={() => setSelectedLoginForm('ldap')} />
                                {!oktaIsOnlyProvider && (
                                    <CustomButton text="Sign in with OpenID Connect" onClick={() => setSelectedLoginForm('oidc')} />
                                )}
                            </>
                        )}
                    </>
                );
            case 'ldap':
                return <LdapLoginForm setSelectedLoginForm={setSelectedLoginForm} />;
            case 'oidc':
                return <OidcLoginForm setSelectedLoginForm={setSelectedLoginForm} />;
            default:
                return null;
        }
    };
    return renderLoginForm();
}


export default LoginTab;
